import { CustomIconName } from "./customIconName"
import { CustomLocalIconName } from "./customLocalIconName"

// Purposeful design choice to have duplicates, since icons can change often until final production release
/* eslint-disable @typescript-eslint/no-duplicate-enum-values */

/**
 * This enum contains all the needed font awesome icons which are readily available.
 *
 * This list is compiled based on the list of icons in Figma.
 * https://www.figma.com/file/8XOze4uCISh4YozT6y2sTB/SafeEx-APP?node-id=9755%3A82890&t=oeAs1emDc8OwXf7i-4
 *
 * The following list is missing the custom icons. You can find them in customIconName.ts
 */
enum FontAwesomeIconName {
  // General User Interface
  ARROW_ROTATE_RIGHT = "arrow-rotate-right",
  EXPAND = "angle-down",
  COLLAPSE = "angle-up",
  INTERNAL_LINK = "arrow-right-to-arc",
  EXTERNAL_LINK = "up-right-from-square",
  BACK = "angle-left",
  SUCCEEDED = "circle-check",
  SUCCEEDED_SOLID = "fa-solid fa-circle-check",
  INFORMATION = "circle-info",
  ISSUE = "circle",
  ISSUE_SOLID = "fa-solid fa-circle",
  MANDATORY = "asterisk",
  OPEN_CALENDAR = "calendar",
  ADD = "plus",
  REMOVE = "minus",
  EXPAND_IMAGE = "arrows-maximize",
  EXPAND_GALLERY_IMAGE = "expand",
  COMPRESS_GALLERY_IMAGE = "compress",
  EARTH = "earth-europe",
  REJECT = "xmark",
  REJECT_CIRCLE = "circle-x",
  ACCEPT = "check",
  TICK = "check",
  RESET = "rotate-left",
  BLUETOOTH = "bluetooth",
  CHANGE_REQUEST = "pen-to-square",
  IMAGE_PLACEHOLDER = "image",
  CLOSE = "xmark",
  CLOSE_LARGE = "xmark-large",
  SEARCH = "magnifying-glass",
  HIDE = "eye-slash",
  SHOW = "eye",
  LINK_ICON_FORWARD = "angle-right",
  ERROR = "triangle-exclamation",
  CONTEXT_MENU = "ellipsis-vertical",
  CHECKBOX_CHECKED = "square-check",
  CHECKBOX_NOT_CHECKED = "square",
  ARROW_RIGHT = "arrow-right",
  CERTIFICATE_REQUIRED = "file-certificate",
  CLICKABLE = "hand-pointer",
  WARNING = "triangle-exclamation",
  WARNING_SOLID = "fa-solid fa-triangle-exclamation",
  DOWNLOAD = "download",
  UPLOAD = "upload",
  ADD_TO_CALENDAR = "calendar-plus",
  CUSTOMIZE = "sliders-simple",
  SELECT_FROM_LIST = "list-dropdown",
  SORT = "sort",
  SORT_UP = "sort-up",
  SORT_DOWN = "sort-down",
  LIST_DISPLAY = "list-check",
  DRAW_TETRAGON = "draw-square",
  DRAW_POLYGON = "draw-polygon",
  EDIT = "pencil",
  CLEAR = "circle-x",
  EMAIL = "at",
  NOTIFICATION_FROM_SUPPORT = "trumpet",
  CLOCK = "clock",
  CLOCK_ROTATE_LEFT = "clock-rotate-left",
  BEHIND = "turtle",
  SHEEP = "sheep",
  ARROW_DOWN = "arrow-down",
  UNDO = "undo",
  NFC = "nfc-symbol",
  WRENCH = "screwdriver-wrench",
  SAVED_CONFIGURATIONS = "bookmark",
  NOT_FAVOURITE = "star",
  FAVOURITE = "fa-solid fa-star",
  SAVE = "floppy-disk",
  TABLE_VIEW = "list-ul",
  CARD_VIEW = "grid-2",
  OFFLINE = "cloud-slash",
  ONLINE = "cloud",
  MANAGED_WORKSPACE = "people-roof",
  LESS_THAN = "less-than",
  LESS_THAN_EQUAL = "less-than-equal",
  GREATER_THAN = "greater-than",
  GREATER_THAN_EQUAL = "greater-than-equal",
  WORK_ORDER_RESOURCE = "person-circle-exclamation",
  WORK_ORDER_CLOSED_UNCOMPLETED = "clipboard-list-check",
  WORK_ORDER_COMPLETED = "clipboard-check",
  FILTER = "filter",
  USER_WITH_HELMET_REGULAR = "fa-regular fa-user-helmet-safety",
  USER_WITH_HELMET_SOLID = "fa-solid fa-user-helmet-safety",
  GANTT_CHART = "chart-gantt",

  // Main Menu Icons
  HOME = "house",
  TODO = "list-ul",
  WORK_ORDERS = "clipboard-list",
  MORE = "triangle",
  LESS = "triangle fa-rotate-180",
  ITEM = "tag",
  AREAS = "map",
  LAYOUTS = "layer-group",
  MODEL = "cube",
  LOCATION = "location-pin",
  ATTACHMENTS = "paperclip",
  SETTINGS = "user-gear",
  USER_PROFILE = "user",
  USER_CIRCLE = "user-circle",
  ANALYTICS = "chart-column",
  CHEVRON_RIGHT = "chevron-right",
  CHEVRON_LEFT = "chevron-left",
  CHEVRON_UP = "chevron-up",
  CHEVRON_DOWN = "chevron-down",
  CHEVRONS_DOWN = "chevrons-down",
  ADMINISTRATION = "users-gear",
  CHANGE_REQUESTS = "right-left",

  // Context Menu Icons
  LOGIN_HELP = "life-ring",
  // TODO: Missing icon css in Figma
  REPLACE_ITEM = "svg",
  MARK_AS_READ = "envelope-open",
  MARK_AS_UNREAD = "envelope",
  ADD_PHOTO = "camera",
  DELETE = "trash",
  HISTORY = "scroll-old",
  NOTIFICATIONS = "bell",
  NOTIFICATIONS_NEW = "bell-on",
  HELP = "circle-question",
  RETIRE = "box-archive",
  WORK_OBSTRUCTED = "ban fa-rotate-45",
  // TODO: Wrong css in Figma
  WORK_REJECT = "ban",

  // Entity Not Found Icons
  NO_PHOTOS = "image-slash",
  NO_LOCATIONS = "location-pin-slash",
  ATTRIBUTES = "chart-radar",

  // Following icons are not from Figma, but related to Quasar components
  SQUARE_MINUS = "square-minus",
  SQUARE_PLUS = "square-plus",
}

/**
 * IMPORTANT:
 *
 * Use only the following constant when you want to call an icon in the UI
 */
export const Icons = { ...CustomIconName, ...FontAwesomeIconName, ...CustomLocalIconName }
